// Localisable UI strings for the aurora components. Every field is optional;
// AURORA_DEFAULT_LABELS keeps the original English copy, so existing usages
// that pass no labels render exactly as before.
import type { AuroraLevel } from '@/lib/aurora';

export interface AuroraLevelText {
  label?: string;
  description?: string;
  advice?: string;
}

export interface AuroraLabels {
  // AuroraDashboard
  unavailable?: string;
  kpTitle?: string;
  kpNote?: string;
  magnetometersTitle?: string;
  magnetometersNote?: string;
  cloudCoverTitle?: string;
  cloudNote?: string;
  trends?: { rising?: string; falling?: string; stable?: string };
  activity?: { quiet?: string; moderate?: string; high?: string; storm?: string };
  // AuroraStatusBanner
  bannerEyebrow?: string;
  bannerKpLabel?: string;
  bannerRIndexLabel?: string;
  bannerUpdatedPrefix?: string;
  cloudStatus?: {
    unavailable?: string;
    clear?: string;
    partly?: string;
    cloudy?: string;
    overcast?: string;
  };
  time?: {
    justNow?: string;
    minuteAgo?: string;
    minutesAgo?: string; // {count} placeholder
    hourAgo?: string;
    hoursAgo?: string; // {count} placeholder
  };
  levels?: Partial<Record<AuroraLevel, AuroraLevelText>>;
  // SolarWindPanel
  solarTitle?: string;
  solarUnavailable?: string;
  bzTitle?: string;
  bzNote?: string;
  speedTitle?: string;
  speedNote?: string;
  densityTitle?: string;
  densityNote?: string;
  // KpForecastChart
  kpChartTitle?: string;
  kpChartVisible?: string;
  // AuroraCalendar
  calendarTitle?: string;
  calendarSubtitle?: string;
  calendarHeaders?: {
    month?: string;
    darkHours?: string;
    season?: string;
    typicalKp?: string;
    notes?: string;
  };
  calendarMonths?: Record<string, string>;
  calendarNotes?: Record<string, string>;
  // AuroraAlert
  alertRed?: string;
  alertAmber?: string;
  alertCta?: string;
  alertDismiss?: string;
  // AuroraWidget
  widgetTitle?: string;
  widgetUnavailable?: string;
  widgetTooLight?: string;
  widgetCta?: string;
  // AuroraProbabilityMap
  mapAlt?: string;
  mapProbability?: string;
  mapCaption?: string;
  mapForecastTime?: string;
  mapSource?: string;
}

export const AURORA_DEFAULT_LABELS = {
  unavailable: 'Aurora forecast unavailable. Please try again later.',
  kpTitle: 'Kp Index',
  kpNote: 'Planetary geomagnetic activity',
  magnetometersTitle: 'Magnetometer Stations',
  magnetometersNote: 'Local magnetometers - R-index readings',
  cloudCoverTitle: 'Cloud Cover',
  cloudNote: 'You need clear skies to see aurora',
  trends: { rising: 'rising', falling: 'falling', stable: 'stable' },
  activity: { quiet: 'quiet', moderate: 'moderate', high: 'high', storm: 'storm' },
  bannerEyebrow: 'Kettura Aurora Forecast',
  bannerKpLabel: 'Kp Index:',
  bannerRIndexLabel: 'Muonio R-index:',
  bannerUpdatedPrefix: 'Updated',
  cloudStatus: {
    unavailable: 'Cloud data unavailable',
    clear: 'Clear skies',
    partly: 'Partly cloudy',
    cloudy: 'Cloudy',
    overcast: 'Overcast',
  },
  time: {
    justNow: 'just now',
    minuteAgo: '1 minute ago',
    minutesAgo: '{count} minutes ago',
    hourAgo: '1 hour ago',
    hoursAgo: '{count} hours ago',
  },
  levels: {
    green: {
      label: 'Low',
      description: 'Geomagnetic activity is quiet. Aurora unlikely tonight.',
      advice:
        'Enjoy the stars - the Lapland sky is spectacular even without aurora. Check back later as conditions can change rapidly.',
    },
    yellow: {
      label: 'Moderate',
      description: 'Some geomagnetic activity detected. Faint aurora possible.',
      advice:
        'Worth stepping outside after dark if skies are clear. Look north and be patient - aurora can appear suddenly. Best viewing 10pm-2am.',
    },
    amber: {
      label: 'High',
      description: 'Significant geomagnetic activity. Good chance of visible aurora.',
      advice:
        "Head outside! Clear skies and dark locations away from village lights give the best views. Your cabin's hot tub is an ideal viewing spot.",
    },
    red: {
      label: 'Very High',
      description: 'Strong geomagnetic storm. Bright, vivid aurora very likely.',
      advice:
        'Drop everything and get outside NOW. This is a rare, powerful display - the sky could be alive with colour. Set your phone camera to night mode.',
    },
  },
  solarTitle: 'Solar Wind',
  solarUnavailable: 'Solar wind data unavailable. Please try again later.',
  bzTitle: 'Bz Component',
  bzNote: 'Negative Bz = aurora favorable',
  speedTitle: 'Solar Wind Speed',
  speedNote: 'Fast solar wind drives aurora',
  densityTitle: 'Solar Wind Density',
  densityNote: 'Higher density = stronger impact',
  kpChartTitle: '3-Day Kp Forecast',
  kpChartVisible: 'Visible in Lapland',
  calendarTitle: 'Monthly Aurora Calendar',
  calendarSubtitle: 'Viewing Guide',
  calendarHeaders: {
    month: 'Month',
    darkHours: 'Dark Hours',
    season: 'Aurora Season?',
    typicalKp: 'Typical Kp',
    notes: 'Notes',
  },
  alertRed: "AURORA STORM - Vivid northern lights expected tonight! Don't miss this",
  alertAmber: 'Aurora Alert - Good chance of northern lights tonight!',
  alertCta: 'Check the forecast →',
  alertDismiss: 'Dismiss aurora alert',
  widgetTitle: "🦊 Fox's Fire Forecast",
  widgetUnavailable: 'Aurora data unavailable',
  widgetTooLight: 'Too light for aurora',
  widgetCta: 'Full aurora forecast →',
  mapAlt: 'Aurora forecast map - northern hemisphere',
  mapProbability: 'Lapland probability:',
  mapCaption: 'NOAA OVATION Aurora Model — 30 min forecast',
  mapForecastTime: 'Forecast time:',
  mapSource: 'Source: NOAA OVATION Model',
} as const;

/** Shallow-merge provided labels over the English defaults (nested groups included). */
export function resolveAuroraLabels(labels?: AuroraLabels) {
  const d = AURORA_DEFAULT_LABELS;
  return {
    ...d,
    ...labels,
    trends: { ...d.trends, ...labels?.trends },
    activity: { ...d.activity, ...labels?.activity },
    cloudStatus: { ...d.cloudStatus, ...labels?.cloudStatus },
    time: { ...d.time, ...labels?.time },
    levels: {
      green: { ...d.levels.green, ...labels?.levels?.green },
      yellow: { ...d.levels.yellow, ...labels?.levels?.yellow },
      amber: { ...d.levels.amber, ...labels?.levels?.amber },
      red: { ...d.levels.red, ...labels?.levels?.red },
    },
    calendarHeaders: { ...d.calendarHeaders, ...labels?.calendarHeaders },
    calendarMonths: labels?.calendarMonths ?? {},
    calendarNotes: labels?.calendarNotes ?? {},
  };
}

export type ResolvedAuroraLabels = ReturnType<typeof resolveAuroraLabels>;
